import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { CollectConsignmentWithDate, FormOption } from 'types';
import { Button, Checkbox, Text, Tooltip } from 'components';
import { curtailStringWithEllipsis, setLocalStorageItem } from 'helpers';
import {
  FontSize,
  LocalStorageKeys,
  TEST_IDS,
  deliveryModes,
  routes,
} from 'commons';
import * as S from './styles';
import { CollectConsignment } from '../';

interface Props {
  carrier: FormOption;
  consignmentList: CollectConsignmentWithDate[];
  driverName: string;
  defaultOpen?: boolean;
  handleSelectAllConsignments?: (
    consignmentCodes: string[],
    remove?: boolean
  ) => void;
  handleSelectConsignment?: (consignmentCode: string) => void;
}

export function Carrier({
  carrier,
  consignmentList,
  driverName,
  defaultOpen,
  handleSelectAllConsignments,
  handleSelectConsignment,
}: Props) {
  const [isCardOpen, setIsCardOpen] = useState(defaultOpen);

  const navigate = useNavigate();

  const driverNameFormatted = curtailStringWithEllipsis(driverName, 10);

  const selectConsignmentsForm = useFormik({
    initialValues: (() => {
      const selectedConsignments: string[] = [];

      return { selectedConsignments };
    })(),
    onSubmit: (values) => {
      const { selectedConsignments } = values;

      const resultConsignments = consignmentList.filter((consignment) =>
        selectedConsignments.some(
          (code) => consignment.consignmentCode === code
        )
      );

      setLocalStorageItem(
        LocalStorageKeys.ConsignmentsToCollect,
        resultConsignments
      );

      navigate(routes.confirmCollect.path);
    },
  });

  const { handleSubmit, dirty, setFieldValue, values } = selectConsignmentsForm;

  useEffect(() => {
    const { values } = selectConsignmentsForm;

    const continueChecked = values.selectedConsignments.filter((code) => {
      const isNotOnFilteredList = consignmentList.every(
        (consignment) => consignment.consignmentCode !== code
      );

      if (isNotOnFilteredList) {
        handleCheckboxChange(code);
      }

      return !isNotOnFilteredList;
    });

    setFieldValue('selectedConsignments', continueChecked);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignmentList]);

  function handleCheckboxChange(consignmentCode: string) {
    const selectedConsignments = values.selectedConsignments;
    const isConsignmentCodeOnTheList =
      selectedConsignments.includes(consignmentCode);

    handleSelectConsignment && handleSelectConsignment(consignmentCode);

    if (!isConsignmentCodeOnTheList) {
      setFieldValue('selectedConsignments', [
        ...selectedConsignments,
        consignmentCode,
      ]);
    } else {
      const currentConsignments = selectedConsignments.filter(
        (code) => code !== consignmentCode
      );
      setFieldValue('selectedConsignments', currentConsignments);
    }
  }

  function toggleSelectAll(e: React.ChangeEvent<HTMLInputElement>) {
    const consignmentCodes = consignmentList.map(
      (consignment) => consignment.consignmentCode
    );

    const { checked } = e.currentTarget;

    if (checked) {
      handleSelectAllConsignments &&
        handleSelectAllConsignments(consignmentCodes);

      setFieldValue('selectedConsignments', consignmentCodes);

      setIsCardOpen(true);
    } else {
      handleSelectAllConsignments &&
        handleSelectAllConsignments(consignmentCodes, true);
      setFieldValue('selectedConsignments', []);
    }
  }

  const renderConsignmentList = consignmentList.map((consignment, index) => {
    const { consignmentCode } = consignment;

    const mottuDeliveryMode = deliveryModes.find(
      (deliveryMode) => 'Mottu' === deliveryMode.label
    );

    const hasCarrierCollectCode = carrier.value === mottuDeliveryMode?.value;

    return (
      <CollectConsignment
        key={consignmentCode}
        consignment={consignment}
        id={consignmentCode}
        hasCarrierCollectCode={hasCarrierCollectCode}
        name={`selectedConsignments[${index}]`}
        checked={values.selectedConsignments.includes(consignmentCode)}
        onChange={() => handleCheckboxChange(consignmentCode)}
      />
    );
  });

  const consignmentQuantityText = `${consignmentList.length} pedido${
    consignmentList.length > 1 ? 's' : ''
  }`;

  return (
    <S.Wrapper onSubmit={handleSubmit}>
      <S.HeaderWrapper>
        <S.TitleWrapper>
          <S.CheckboxAndLabel>
            <Checkbox
              id={carrier.label}
              key={values.selectedConsignments.join(',')}
              variant="primary"
              onChange={toggleSelectAll}
              checked={
                consignmentList.length === values.selectedConsignments.length
              }
            />
            <S.Label htmlFor={carrier.label}>{carrier.label}</S.Label>
          </S.CheckboxAndLabel>
          {driverName !== '' && (
            <S.DriverName>
              <Tooltip message={driverName}>
                <Text size={FontSize.font12}>{driverNameFormatted}</Text>
              </Tooltip>
            </S.DriverName>
          )}
        </S.TitleWrapper>
        <S.QuantityAndToggleButton>
          <Text size={FontSize.font12}>{consignmentQuantityText}</Text>
          <S.IconButton
            onClick={() => {
              setIsCardOpen((prevState) => !prevState);
            }}
          >
            {isCardOpen ? (
              <IoIosArrowUp size={24} />
            ) : (
              <IoIosArrowDown size={24} />
            )}
          </S.IconButton>
        </S.QuantityAndToggleButton>
      </S.HeaderWrapper>

      <S.ConsignmentsAccordion $isCardOpen={isCardOpen}>
        <S.Consignments data-testid={TEST_IDS.CONSIGNMENT_LIST_DIV}>
          {renderConsignmentList}
        </S.Consignments>

        <Button
          data-testid={TEST_IDS.CONFIRM_SELECTION_BUTTON}
          disabled={!dirty}
          full
          type="submit"
        >
          Registrar retirada
        </Button>
      </S.ConsignmentsAccordion>
    </S.Wrapper>
  );
}
